/* global JSX */
import { Box, Grid, Tooltip } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { Translations } from "ui/Scripts/source/models/translations";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import * as riskService from "../../../models/services/riskService";
import { ISessionState } from "../../../models/session/sessionState";
import { AddButton } from "../../framework/Button/AddButton";
import { IListUsingComponentState, List } from "../../framework/List/List";
import { ListActionButtons } from "../../framework/List/ListActionButtons";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { ListToggleButtons } from "../../framework/List/ListToggleButtons";
import { PageTitle } from "../../framework/Page/PageTitle";
import { ListSearchContainer } from "source/components/framework/List/ListSearchContainer";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { PageListActionContainer } from "source/components/framework/List/PageListActionContainer";
import { TrashBinButton } from "source/components/framework/Button/TrashBinButton";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { RestoreFromTrashBinButton } from "source/components/framework/Button/RestoreFromTrashBinButton/RestoreFromTrashBinButton";
import { EmptyTrashBinButton } from "source/components/framework/Button/EmptyTrashBinButton";
import { PreviewToggleButton } from "source/components/framework/Button/PreviewToggleButton";
import { getRiskPageGeneralFilterFromFilterState, IRiskPageGeneralFilter, IRiskPageFilterState, getRiskPageGeneralFilterChange } from "../RiskPage/RiskPageUtils";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { IPageNavMenuItem } from "source/components/framework/PageNav/PageNavMenuItems";
import { SettingsButton } from "source/components/framework/Button/SettingsButton";
import { IRiskListItem, RiskListItem } from "ui/Scripts/source/models/risk/riskListItem";
import { AppUtils } from "source/framework/appUtils";
import { useEffect, useRef, useState } from "react";
import ListLinkValue from "source/components/framework/List/ListLinkValue/ListLinkValue";
import { RiskListStateCol } from "./RiskListStateCol";
import { RiskListLevelChip } from "./RiskListLevelChip";
import { RiskListTraceabilityChip } from "./RiskListTraceabilityChip";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { ListCardField } from "source/components/framework/List/ListCardField";
import { ListDetailsButton } from "source/components/framework/List/ListDetailsButton";
import { RiskListApprovalChip } from "./RiskListApprovalChip";
import { RiskEditDialog } from "../RiskEditDialog";
import { ConfirmationDialogResult, RiskType } from "ui/Scripts/source/models/enums";
import { AppAddIcon } from "source/components/framework/Icon/AppAddIcon";
import { AppAddFromTemplateIcon } from "source/components/framework/Icon/AppAddFromTemplateIcon";
import { RiskTemplateNewDialog } from "../RiskSettingsDialog/RiskTemplateGrid/RiskTemplateNewDialog";
import { RiskListDetailsPanel } from "./RiskListDetailsPanel";
import { AppInfoIcon } from "ui/Scripts/source/components/framework/Icon/AppInfoIcon";
import { optionCodeRiskMatrixInfoDocument } from "ui/Scripts/source/models/consts";
import { RiskListCategoryCol } from "./RiskListCategoryCol";

interface IRiskListProp extends IRiskPageFilterState {
    sessionState: ISessionState;
    filterName: string;
    companyId: string;
    canCreateRisks: boolean;
    listFilterIsSet: boolean;
    units: IIdTitleNode[];
    dataId?: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    settingsActions: IPageNavMenuItem[];
    trashBin: boolean;
    titleSuffix?: JSX.Element;
    reportTabs?: React.ReactNode;
    showDocumentPreview?: boolean;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    onLoadPageData: () => void;
    onOpenFiltersDialog: () => void;
    onOpenTrashBinDialog: () => void;
    onChangeShowDocumentPreview?: (showDocumentPreview: boolean) => void;
    onToggleSettingsDialog: () => void;
}

interface IRiskListSearchFilter extends IRiskPageGeneralFilter {
    pageSize: number;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
}

interface IRiskListState extends IListUsingComponentState<IRiskListItem>, IRiskListSearchFilter {
    // List
    intialized: boolean;
    isLoading: boolean;
    dataId: string;
    companyId: string;

    listModeIsLine: boolean;
    checkedIds: string[];

    // Details
    detailsDataId: string;

    // Dialog
    editDialogRiskType: RiskType;
    editDialogRiskTemplateId: string;

    // AddFromTemplateDialog
    showAddFromTemplateDialog: boolean;
}

export const RiskList = (props: IRiskListProp) => {
    const _isMounted = useRef(false);
    const sentReadReceipts = useRef<string[]>([]);
    const [state, setState] = useState<IRiskListState>({
        ...getRiskPageGeneralFilterFromFilterState(props),
        intialized: false,
        isLoading: false,
        dataId: props.dataId,
        companyId: props.companyId,
        page: 1,
        hasMore: true,
        filter: "",
        pageSize: infiniteListPageSize,
        sortColumn: props.sortColumn,
        sortOrderIsAsc: props.sortOrderIsAsc,
        listModeIsLine: true,
        items: [],
        selectedId: null,
        checkedIds: [],
        // Details
        detailsDataId: "",
        detailsItem: null,
        // Dialog
        showEditDialog: false,
        editDialogId: "",
        editDialogHasPrevious: false,
        editDialogHasNext: false,
        editDialogRiskType: RiskType.Risk,
        editDialogRiskTemplateId: null,
        // AddFromTemplateDialog
        showAddFromTemplateDialog: false
    });

    const setReadReceipt = (detailsItem: IRiskListItem) => {
        if (!detailsItem || detailsItem.isRead) return;
        const userIsCreator = detailsItem.createUser.code === props.sessionState.code;
        if (userIsCreator || sentReadReceipts.current.indexOf(detailsItem.id) > -1) return;
        const result = AppUtils.callService(() => riskService.sendRiskReadReceipt(props.companyId, detailsItem.id), false, true);
        if (!result) return;
        sentReadReceipts.current.push(detailsItem.id);
        const items = state.items.slice(0);
        const item = items.find(i => i.id === detailsItem.id);
        if (!item) return;
        item.isRead = true;
        setState(old => ({
            ...old,
            items
        }));
    };

    const searchItems = async (searchFilter: IRiskListSearchFilter, resetItems: boolean, aRefreshList: boolean, moveNext: boolean = false, setDialogData: boolean = true, newSelectedId: string = null) => {
        setState(old => ({ ...old, isLoading: true }));
        const result = await AppUtils.callService(() => riskService.getRiskListItems(props.companyId, !aRefreshList ? searchFilter.pageSize : (searchFilter.page * searchFilter.pageSize), !aRefreshList ? searchFilter.page : 1,
            props.trashBin, searchFilter.filter, searchFilter.typeIds, searchFilter.unitIds, searchFilter.stateIds, searchFilter.riskCategoryIds, searchFilter.riskProcessIds,
            searchFilter.riskStandardIds, searchFilter.riskStakeholderIds, searchFilter.residualRiskIds, searchFilter.riskTreatmentStateIds, searchFilter.handlerUserCodes, searchFilter.owners,
            searchFilter.sortColumn, searchFilter.sortOrderIsAsc), false, false, () => setState(old => ({ ...old, isLoading: false })));
        if (!result) return;
        const items = List.getListItems(state.items, newSelectedId ?? state.selectedId, state.checkedIds, result.items, resetItems, aRefreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let detailsItem = index > -1 ? items.items[index] : null;
        if (moveNext && detailsItem && index < items.items.length - 1) {
            index = index + 1;
            detailsItem = items.items[index];
            selectedId = items.items[index].id;
        }
        setState(old => ({
            ...old,
            ...searchFilter,
            intialized: true,
            page: !aRefreshList ? result.page : searchFilter.page,
            hasMore: result.hasMore,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            detailsItem,
            editDialogId: setDialogData ? (moveNext && detailsItem ? detailsItem.id : "") : old.editDialogId,
            editDialogHasPrevious: setDialogData ? index > 0 : old.editDialogHasPrevious,
            editDialogHasNext: setDialogData ? (index < items.items.length - 1 || result.hasMore) : old.editDialogHasNext,
        }));
        // props.onSetSortData(searchFilter.sortColumn, searchFilter.sortOrderIsAsc);
        // setReadReceipt(detailsItem);
    };

    const getRiskListSearchFilter = (aState: IRiskListState = null): IRiskListSearchFilter => {
        const uState = aState ?? state;
        return {
            typeIds: uState.typeIds,
            unitIds: uState.unitIds,
            stateIds: uState.stateIds,
            riskCategoryIds: uState.riskCategoryIds,
            riskProcessIds: uState.riskProcessIds,
            riskStandardIds: uState.riskStandardIds,
            riskStakeholderIds: uState.riskStakeholderIds,
            residualRiskIds: uState.residualRiskIds,
            riskTreatmentStateIds: uState.riskTreatmentStateIds,
            handlerUserCodes: uState.handlerUserCodes,
            owners: uState.owners,
            pageSize: uState.pageSize,
            page: uState.page,
            filter: uState.filter,
            sortColumn: uState.sortColumn,
            sortOrderIsAsc: uState.sortOrderIsAsc
        };
    };

    const refreshList = async (setDialogData: boolean = false, newSelectedId: string = null, updateDetails: boolean = false) => {
        await searchItems(getRiskListSearchFilter(), false, true, false, setDialogData, newSelectedId);
        if (!updateDetails) return;
        setState(old => ({ ...old, detailsDataId: Base.getGuid() }));
    };

    useEffect(() => {
        const filterStateChange = getRiskPageGeneralFilterChange(state, props);
        if (state.intialized &&
            props.dataId === state.dataId && props.companyId === state.companyId &&
            filterStateChange.notChanged &&
            state.sortColumn === props.sortColumn && state.sortOrderIsAsc === props.sortOrderIsAsc) return;
        setState(old => ({
            ...old,
            dataId: props.dataId,
            companyId: props.companyId,
        }));
        searchItems({
            ...getRiskPageGeneralFilterFromFilterState(props),
            pageSize: state.pageSize,
            page: state.page,
            filter: state.filter,
            sortColumn: props.sortColumn,
            sortOrderIsAsc: props.sortOrderIsAsc
        }, true, false, false, false);
    }, [props.dataId, props.companyId, props.sortColumn, props.sortOrderIsAsc,
        props.selectedRiskTypeIds, props.selectedUnitIds, props.selectedStateIds, props.selectedRiskCategoryIds, props.selectedRiskProcessIds,
        props.selectedRiskStandardIds, props.selectedRiskStakeholderIds, props.selectedResidualRiskIds,
        props.selectedRiskTreatmentStateIds, props.selectedHandlerUserCodes, props.selectedOwners]);

    useEffect(() => {
        _isMounted.current = true;
        
        return () => {
            _isMounted.current = false;
        };
    }, []);

    const changeFilter = (filter: string) => {
        searchItems({ ...getRiskListSearchFilter(), filter }, true, false);
    };

    const handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        searchItems({ ...getRiskListSearchFilter(), page: !nextPage ? 1 : state.page + 1, sortColumn, sortOrderIsAsc }, !nextPage, false);
    };

    const handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        const detailsItem = state.items.find(i => i.id === selectedId);
        setState(old => ({
            ...old,
            selectedId,
            checkedIds,
            detailsItem
        }));
        setReadReceipt(detailsItem);
    };

    const handleListModeToggle = (_event: React.MouseEvent<HTMLElement>, listModeIsLine: boolean) => {
        setState(old => ({
            ...old,
            listModeIsLine,
        }));
    };

    // #region Add Risk
    const handleAdd = (riskTemplateId: string = null, riskType: RiskType = RiskType.Risk) => {
        setState(old => ({
            ...old,
            showEditDialog: true,
            editDialogId: Base.emptyGuid,
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            detailsItem: null,
            editDialogRiskType: riskType,
            editDialogRiskTemplateId: riskTemplateId,
            showAddFromTemplateDialog: false
        }));
    };

    const handleAddFromTemplate = () => {
        setState(old => ({
            ...old,
            showAddFromTemplateDialog: true
        }));
    };
    // #endregion Add Risk

    // #region Edit dialog
    const handleOpenLine = (id: string) => {
        setState(old => ({
            ...old,
            ...List.getShowEditDialogStateChange(old, id),
            editDialogRiskType: RiskType.Risk,
            editDialogRiskTemplateId: null
        }));
    };
    
    const handleEditDialogSave = (id: string) => {
        refreshList(false, id);
        props.onLoadPageData();
    };

    const handleEditDialogClose = () => {
        setState(old => ({
            ...old,
            showEditDialog: false,
        }));
        refreshList(false, null, true);
        props.onLoadPageData();
    };

    const setEditDialogItemByIndex = (index: number) => {
        setState(old => ({
            ...old,
            ...List.getEditDialogItemByIndexStateChange(old, index, () => searchItems({ ...getRiskListSearchFilter(old), page: old.page + 1 }, false, false, true))
        }));
    };
    // #endregion Edit dialog

    // #region Print
    // const handlePrintDraft = async () => {
    //     const state = this.state;
    //     if (!state.selectedId) return;
    //     const fileName = await AppUtils.callService(() => riskService.getAppReportDraftPrint(this.props.companyId, state.selectedId));
    //     if (!fileName) return;
    //     AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
    // };

    // const handlePrintPublished = async () => {
    //     const state = this.state;
    //     if (!state.selectedId) return;
    //     const fileName = await AppUtils.callService(() => kService.getAppReportPublishedPrint(this.props.companyId, state.selectedId));
    //     if (!fileName) return;
    //     AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
    // };

    const handlePrint = async () => {
        AppUtils.showErrorMessage(Translations.NotImplementedYet);
        // if (!state.selectedId) return;
        // const item = state.items.find(i => i.id === state.selectedId);
        // if (!item) return;
        // if (item.canEdit) {
        //     await handlePrintDraft();
        // } else {
        //     await handlePrintPublished();
        // }
    };
    // #endregion Print

    const handleEmptyTrashBin = async () => {
        if (await AppUtils.showConfirmationDialog(Translations.EmptyRiskManagementTrashBinConfirmation) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const result = await AppUtils.callService(() => riskService.emptyTrashBin(props.companyId));
        if (!result) return;
        refreshList();
    };

    const handleRestoreFromTrashBin = async () => {
        if (!state.selectedId) return;
        const result = await AppUtils.callService(() => riskService.restoreRiskFromTrashBin(props.companyId, state.selectedId, null));
        if (!result) return;
        refreshList();
    };

    const handleInfoClick = async () => {
        const fileName = await AppUtils.callService(() => riskService.downloadInfoDocument(optionCodeRiskMatrixInfoDocument), false);
        if (!fileName) return;
        Base.openHtmlInNewTab(fileName);
    };

    const beforeList = <>
        {!props.trashBin &&
            <>
                <PageListActionContainer
                    leftItems={
                        props.reportTabs
                    }
                    rightItems={
                        <>
                            <ListActionButtons
                                filterIsSet={props.listFilterIsSet}
                                //printDisabled={!state.selectedId}
                                onFilter={props.onOpenFiltersDialog}
                                //onPrint={handlePrint}
                            />
                            <ListToggleButtons value={state.listModeIsLine} onChange={handleListModeToggle} />
                        </>
                    }
                />
                <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: "1rem" }}>
                    <ListSearchContainer>
                        <ListSearchFilter
                            searchFilter={state.filter}
                            onSearchClick={changeFilter}
                        />
                    </ListSearchContainer>
                    <Tooltip title={Translations.RiskMatrix}>
                        <AppInfoIcon
                            color="info"
                            sx={{ cursor: "pointer" }}
                            onClick={handleInfoClick}
                        />
                    </Tooltip>
                </Box>
            </>
        }
        {props.trashBin &&
            <ActionContainer
                leftItems={
                    <ListSearchFilter
                        searchFilter={state.filter}
                        onSearchClick={changeFilter}
                    />
                }
                rightItems={
                    <>
                        <Box display="flex" sx={{ marginRight: ".5rem" }}>
                            {props.sessionState.isCompanyAdmin &&
                                <EmptyTrashBinButton onClick={handleEmptyTrashBin} disabled={state.items.length < 1} sx={{ marginRight: ".5rem" }}>
                                    {Translations.EmptyRiskManagementTrashBin}
                                </EmptyTrashBinButton>
                            }
                            <RestoreFromTrashBinButton onClick={handleRestoreFromTrashBin} disabled={!state.detailsItem?.canEdit}>
                                {Translations.RestoreRiskOrOpportunityFromTrashBin}
                            </RestoreFromTrashBinButton>
                        </Box>
                        <ListToggleButtons value={state.listModeIsLine} onChange={handleListModeToggle} />
                    </>
                }
            />
        }
    </>;

    const nowTicks = Base.getNowDateTicks();

    const detailsItemIsRisk = state.detailsItem?.riskType === RiskType.Risk;

    const list = <List
        intialized={state.intialized}
        isLoading={state.isLoading}
        hasMore={state.hasMore}
        globalLoadingInUse={true}
        height={!props.trashBin ? "calc(100vh - 296px)" : "100%"}
        header={state.listModeIsLine
            ? {
                columns: [
                    { size: props.showDocumentPreview ? 2 : 1, column: "unitCode", content: Translations.Unit },
                    { size: 2, column: "riskCategoryCode", content: Translations.RiskCategory },
                    { size: 2, column: "state", content: Translations.State },
                    { size: 2, column: "title", content: Translations.Title },
                    props.showDocumentPreview ? undefined : { size: 1, column: "owner", content: Translations.RiskOwner },
                    { size: 1, column: "nextAssesmentDate", content: Translations.NextAssessmentDate },
                    { size: 1, column: "riskLevel", content: Translations.RiskLevel },
                    { size: 1, column: "residualRisk", content: Translations.ResidualRisk },
                    { size: 1, column: "traceability", content: Translations.Traceability },
                ]
            }
            : null}
        lines={state.listModeIsLine
            ? state.items.map((item) => {
                return {
                    columns: [
                        { size: props.showDocumentPreview ? 2 : 1, content: <ListLinkValue onClick={() => handleOpenLine(item.id)}>{item.unitCode}</ListLinkValue> },
                        { size: 2, content: <RiskListCategoryCol item={item} /> },
                        { size: 2, content: <RiskListStateCol item={item} trashBin={props.trashBin} /> },
                        { size: 2, content: item.title },
                        props.showDocumentPreview ? undefined : { size: 1, content: item.owner },
                        {
                            size: 1,
                            content: item.riskType === RiskType.Risk
                                ? <Box component="span" sx={{ color: item.nextAssesmentDate === null || item.nextAssesmentDate != null && item.nextAssesmentDate < nowTicks ? "error.main" : undefined }}>{item.nextAssesmentDate ? Base.timeToDateStr(item.nextAssesmentDate) : Translations.Missing}</Box>
                                : ""
                        },
                        { size: 1, content: item.hasAssesment ? <RiskListLevelChip riskLevel={item.riskLevel} /> : "" },
                        { size: 1, content: item.hasAssesment ? <RiskListLevelChip riskLevel={item.residualRisk} /> : "" },
                        { size: 1, content: item.hasAssesment ? <RiskListTraceabilityChip riskLevel={item.residualRisk} /> : "" },
                    ],
                    id: item.id
                };
            })
            : null
        }
        cards={!state.listModeIsLine
            ? state.items.map((item) => {
                const isRisk = item.riskType === RiskType.Risk;
                return {
                    sm: 4,
                    rows: [
                        {
                            cols: [
                                {
                                    size: 6,
                                    title: Translations.Unit,
                                    value: <div><ListLinkValue onClick={() => handleOpenLine(item.id)}>{item.unitCode}</ListLinkValue></div>
                                },
                                { size: 6, title: Translations.RiskLevel, value: item.hasAssesment ? <RiskListLevelChip riskLevel={item.riskLevel} /> : "" },
                            ]
                        },
                        {
                            cols: [
                                { size: 6, title: Translations.AType, value: RiskListItem.getRiskTypeText(item.riskType, item.opportunity) },
                                { size: 6, title: Translations.RiskCategory, value: item.riskCategoryCode },
                            ]
                        },
                        {
                            cols: isRisk
                                ? [
                                    { size: 6, title: Translations.ResidualRisk, value: item.hasAssesment ? <RiskListLevelChip riskLevel={item.residualRisk} /> : "" },
                                    { size: 6, title: Translations.Traceability, value: item.hasAssesment ? <RiskListTraceabilityChip riskLevel={item.residualRisk} /> : "" },
                                ]
                                : [
                                    { size: 12, title: Translations.OpportunityBenefit, value: item.opportunityBenefit },
                                ]
                        },
                        {
                            cols: isRisk
                                ? [
                                    { size: 6, title: Translations.State, value: <RiskListStateCol item={item} trashBin={props.trashBin} /> },
                                    { size: 6, title: Translations.Approval, value: item.hasAssesment ? <RiskListApprovalChip approval={item.approval} /> : "" }
                                ]
                                : [
                                    { size: 12, title: Translations.OpportunityDescription, value: item.opportunityDescription },
                                ]
                        }],
                    id: item.id
                };
            })
            : null
        }
        lineDetails={state.detailsItem && !props.showDocumentPreview
            ? <Box position="relative">
                <GridRow>
                    <Grid item sm={4}>
                        <GridRow>
                            <Grid item sm={6}>
                                <ListCardField title={Translations.ModifyDate} value={Base.timeToDateStr(state.detailsItem.modifyUser.date)} />
                            </Grid>
                            <Grid item sm={6}>
                                <ListCardField title={Translations.State} value={<RiskListStateCol item={state.detailsItem} trashBin={props.trashBin} />} />
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <ListCardField title={Translations.Code} value={state.detailsItem.code} />
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <ListCardField title={Translations.Title} value={state.detailsItem.title} />
                            </Grid>
                        </GridRow>
                    </Grid>
                    <Grid item sm={4}>
                        <GridRow>
                            <Grid item sm={12}>
                                <ListCardField title={Translations.Unit} value={state.detailsItem.unitCode} />
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                {(detailsItemIsRisk
                                    ? <ListCardField title={Translations.Description} value={state.detailsItem.description} />
                                    : <ListCardField title={Translations.OpportunityBenefit} value={state.detailsItem.opportunityBenefit} />
                                )}
                            </Grid>
                        </GridRow>
                    </Grid>
                    <Grid item sm={4}>
                        {(detailsItemIsRisk
                            ? <>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <ListCardField title={Translations.RiskThreat} value={state.detailsItem.threat} />
                                    </Grid>
                                </GridRow>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <ListCardField title={Translations.RiskControls} value={state.detailsItem.controls} />
                                    </Grid>
                                </GridRow>
                            </>
                            : <GridRow>
                                <Grid item sm={12}>
                                    <ListCardField title={Translations.OpportunityDescription} value={state.detailsItem.opportunityDescription} />
                                </Grid>
                            </GridRow>
                        )}
                    </Grid>
                </GridRow>
                <ListDetailsButton
                    onClick={() => handleOpenLine(state.selectedId)}
                />
            </Box>
            : null}
        sortColumn={state.sortColumn}
        sortOrderIsAsc={state.sortOrderIsAsc}
        selectedId={state.selectedId}
        checkedIds={state.checkedIds}
        onSetSelectedIds={handleSetSelectedIds}
        onSearchLines={handleSearchLines}
        onOpenLine={handleOpenLine}
    />;

    return (
        <>
            <div style={{ width: "100%", position: "relative", flexBasis: 0, flexGrow: 7, display: "flex" }}>
                {!props.trashBin &&
                    <PageTitle title={Translations.RiskManagement + (props.filterName ? " - " + props.filterName : (props.listFilterIsSet || state.filter ? " - " + Translations.Filtered : ""))} position="absolute">
                        {props.settingsActions.length > 0 &&
                            <SettingsButton onClick={props.onToggleSettingsDialog} sx={{ marginRight: "24px" }}>
                                {Translations.Settings}
                            </SettingsButton>
                        }
                        {props.canCreateRisks &&
                            <TrashBinButton onClick={props.onOpenTrashBinDialog}>
                                {Translations.TrashBin}
                            </TrashBinButton>
                        }
                        {props.canCreateRisks &&
                            <AddButton
                                sx={{ marginRight: "24px" }}
                                onClick={() => handleAdd()}
                                actions={[
                                    { title: Translations.AddRisk, icon: <AppAddIcon />, onClick: () => handleAdd(null, RiskType.Risk) },
                                    { title: Translations.AddOpportunity, icon: <AppAddIcon />, onClick: () => handleAdd(null, RiskType.Opportunity) },
                                    { title: Translations.AddRiskFromTemplate, icon: <AppAddFromTemplateIcon />, onClick: handleAddFromTemplate }
                                ]}>
                                {Translations.Add}
                            </AddButton>
                        }
                        <PreviewToggleButton
                            value="preview"
                            selected={props.showDocumentPreview}
                            onChange={() => props.onChangeShowDocumentPreview(!props.showDocumentPreview)}
                            style={{ padding: "7px" }}
                        />
                    </PageTitle>
                }
                {(!props.trashBin
                    ? <MainListContainer sx={{ width: "100%" }}>
                        {beforeList}
                        {list}
                    </MainListContainer>
                    : <VerticalContainer
                        width="100%"
                        before={beforeList}
                        main={list}
                    />
                )}
            </div>
            {props.showDocumentPreview &&
                <RiskListDetailsPanel
                    sessionState={props.sessionState}
                    companyId={props.companyId}
                    riskId={state.selectedId}
                    editDialogVisible={state.showEditDialog}
                    dataId={state.detailsDataId}
                    onRefreshList={refreshList}
                    onClose={() => props.onChangeShowDocumentPreview(false)}
                />
            }
            {state.showEditDialog &&
                <RiskEditDialog
                    companyId={props.companyId}
                    sessionState={props.sessionState}
                    riskId={state.editDialogId}
                    riskType={state.editDialogRiskType}
                    riskTemplateId={state.editDialogRiskTemplateId}
                    hasPrevious={state.editDialogHasPrevious}
                    hasNext={state.editDialogHasNext}
                    onSaved={handleEditDialogSave}
                    onClose={handleEditDialogClose}
                    onPrint={handlePrint}
                    onRefreshList={() => refreshList()}
                    onPrevious={() => List.moveToPrevious(state, setEditDialogItemByIndex)}
                    onNext={() => List.moveToNext(state, setEditDialogItemByIndex)}
                />
            }
            {state.showAddFromTemplateDialog && (
                <RiskTemplateNewDialog
                    companyId={props.companyId}
                    title={Translations.AddRiskFromTemplate}
                    onOk={handleAdd}
                    onCancel={() => setState(old => ({ ...old, showAddFromTemplateDialog: false }))}
                />
            )}
        </>
    );
};
